import * as z from "zod";

import { Activity, UnitOfTime } from "@/types/policy.enum";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { PolicyRuleInput, RateInput } from "types";
import { arrActivity, arrRateUnitPeriod, arrUnitOfTime, defaultRate } from "../policies.const";

import { AddIcon } from "assets";
import { Button } from "@/components/ui/button";
import { DeleteIcon } from "components/icons";
import { DropdownSelect } from "components/form-control";
import { IconButton } from "components";
import { InputNumber } from "@/components/form-control/input-number";
import { UserClasses } from "./UserClasses";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

const ruleSchema = z.object({
  activity: z.enum(Activity, {
    message: "Activity is required",
  }),
  maxStay: z.number().optional(),
  maxStayUnit: z.enum(UnitOfTime),
  noReturn: z.number().optional(),
  noReturnUnit: z.enum(UnitOfTime),
  userClasses: z.array(z.string()),
  rate: z.array(z.object({ rate: z.number().min(0) }).optional()).optional(),
});

type RuleFormValues = z.infer<typeof ruleSchema>;

export interface AddRuleModalProps {
  ruleData: PolicyRuleInput;
  isOpen: boolean;
  onDismiss: () => void;
  onApply: (input: PolicyRuleInput) => void;
}

export const AddRuleModal = ({ ruleData, isOpen, onDismiss, onApply }: AddRuleModalProps) => {
  const [rates, setRates] = useState<RateInput[]>(ruleData?.rate ?? []);
  const [userClasses, setUserClasses] = useState<string[]>(ruleData?.userClasses ?? []);

  const form = useForm<RuleFormValues>({
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      ...ruleData,
      maxStay: ruleData?.maxStay ?? 0,
      userClasses: ruleData?.userClasses ?? [],
      rate: ruleData?.rate ?? [],
    },
    mode: "all",
  });

  const { handleSubmit, control } = form;

  const handleSave = (values: RuleFormValues) => {
    const newRule = {
      ...values,
      rate: rates,
      userClasses,
    };
    onApply(newRule);
  };

  const handleChangeRate = ({ field, value, idx }: ChangeRateParams) => {
    const newRates = [...rates];
    newRates[idx] = { ...newRates[idx], [field]: value };
    setRates(newRates);
  };

  const addRate = () => {
    setRates((prevState) => [...prevState, defaultRate]);
  };

  const removeRate = (idx: number) => {
    const newRates = [...rates];
    newRates.splice(idx, 1);
    setRates([...newRates]);
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent className="max-h-[calc(100vh-5em)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit a rule</DialogTitle>
          <DialogDescription>Rule information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmitCapture={(event) => {
              event.stopPropagation();
              event.preventDefault();
              handleSubmit(handleSave)(event);
            }}
            className="space-y-6"
          >
            <FormField
              control={control}
              name="activity"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Activity<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <DropdownSelect options={arrActivity} value={value} onValueChange={onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-start gap-4">
              <FormField
                control={control}
                name="maxStay"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max stay</FormLabel>
                    <FormControl>
                      <InputNumber {...field} min={0} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="maxStayUnit"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <DropdownSelect options={arrUnitOfTime} value={value} onValueChange={onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex items-start gap-4">
              <FormField
                control={control}
                name="noReturn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>No return</FormLabel>
                    <FormControl>
                      <InputNumber {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="noReturnUnit"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <DropdownSelect options={arrUnitOfTime} value={value} onValueChange={onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormItem>
              <FormLabel>User Classes</FormLabel>
              <FormControl>
                <>
                  <UserClasses userClasses={userClasses} setUserClasses={setUserClasses} />
                </>
              </FormControl>
              <FormMessage />
            </FormItem>

            <div className="text-medium mb-4">Rates</div>
            <div className="flex flex-col gap-4">
              {rates.map((item, rateIndex) => (
                <div key={`rate-${rateIndex}`} className="flex justify-between items-center">
                  <RateItem item={item} changeValue={handleChangeRate} idx={rateIndex} />
                  <IconButton className="w-6 h-6" onClick={() => removeRate(rateIndex)}>
                    <DeleteIcon className="w-4 h-4 text-gray-400" />
                  </IconButton>
                </div>
              ))}
            </div>
            <div className="flex items-center mb-8">
              <IconButton className="w-6 h-6" onClick={addRate}>
                <AddIcon className="w-4 h-4 text-gray-400" />
              </IconButton>
            </div>

            <DialogFooter className="sm:justify-between">
              <Button variant="secondary" type="button" onClick={onDismiss}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSubmit(handleSave)}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

type ChangeRateParams = { field: keyof RateInput; value: number | string; idx: number };

type RateItemProps = {
  item: RateInput;
  changeValue: (input: ChangeRateParams) => void;
  idx: number;
};

const RateItem = ({ item, changeValue, idx }: RateItemProps) => {
  return (
    <div className="flex-col space-y-4">
      <div className="flex gap-4">
        <FormItem>
          <FormLabel>
            Rate <span className="text-red-500">*</span>
          </FormLabel>
          <FormControl>
            <InputNumber value={item.rate} onChange={(value) => changeValue({ field: "rate", value, idx })} min={0} />
          </FormControl>
        </FormItem>

        <FormItem>
          <FormLabel>Unit</FormLabel>
          <FormControl>
            <DropdownSelect
              options={arrUnitOfTime}
              value={item.rateUnit}
              onValueChange={(value) => changeValue({ field: "rateUnit", value, idx })}
            />
          </FormControl>
        </FormItem>

        <FormItem>
          <FormLabel>Period of time</FormLabel>
          <FormControl>
            <DropdownSelect
              options={arrRateUnitPeriod}
              value={item.rateUnitPeriod}
              onValueChange={(value) => changeValue({ field: "rateUnitPeriod", value, idx })}
            />
          </FormControl>
        </FormItem>
      </div>

      <div className="flex gap-4">
        <FormItem>
          <FormLabel>Increment duration</FormLabel>
          <FormControl>
            <InputNumber
              value={item.incrementDuration}
              onChange={(value) => changeValue({ field: "incrementDuration", value, idx })}
              min={0}
            />
          </FormControl>
        </FormItem>

        <FormItem>
          <FormLabel>Increment amount</FormLabel>
          <FormControl>
            <InputNumber
              value={item.incrementAmount}
              onChange={(value) => changeValue({ field: "incrementAmount", value, idx })}
              min={0}
            />
          </FormControl>
        </FormItem>
      </div>

      <div className="flex gap-4">
        <FormItem>
          <FormLabel>Start duration</FormLabel>
          <FormControl>
            <InputNumber
              value={item.startDuration}
              onChange={(value) => changeValue({ field: "startDuration", value, idx })}
              min={0}
            />
          </FormControl>
        </FormItem>

        <FormItem>
          <FormLabel>End duration</FormLabel>
          <FormControl>
            <InputNumber
              value={item.endDuration}
              onChange={(value) => changeValue({ field: "endDuration", value, idx })}
              min={0}
            />
          </FormControl>
        </FormItem>
      </div>
    </div>
  );
};
